import {
  EnvironmentProviders,
  inject,
  Injector,
  PLATFORM_ID,
  provideEnvironmentInitializer,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { onAuthStateChanged } from 'firebase/auth';
import { TicketPortalStore } from '@solidgroup-agency/feedback-widget';
import { FIREBASE_AUTH } from '@awo-care/shared-firebase';

/**
 * „Meine Anfragen" (#1175): Der Ticket-Store des Feedback-Widgets ist ein
 * App-Singleton und kennt keinen Nutzerwechsel. Ohne Reset sähe nach
 * Abmelden + Anmelden eines anderen Nutzers im selben Tab dieser den Badge und
 * — bis zum Nachladen — die Tickettitel des vorigen Nutzers.
 *
 * Der Store wird erst beim Wechsel geholt: Beim App-Start soll er nicht vor
 * dem Login laden (sein Konstruktor ruft sofort die Ticketliste ab).
 */
export function provideTicketPortalUserReset(): EnvironmentProviders {
  return provideEnvironmentInitializer(() => {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) return;
    const auth = inject(FIREBASE_AUTH);
    const injector = inject(Injector);
    let lastUid: string | null | undefined;

    onAuthStateChanged(auth, (user) => {
      const uid = user?.uid ?? null;
      const changed = isUserChange(lastUid, uid);
      lastUid = uid;
      if (changed) {
        resetTicketPortalStore(injector.get(TicketPortalStore), uid !== null);
      }
    });
  });
}

/**
 * Muss der Store neu laden? Bei Ab-, An- und Ummelden — und beim ersten
 * Auth-Callback mit Nutzer: Beim App-Start lädt der Store, bevor Firebase die
 * Anmeldung wiederhergestellt hat, und bliebe sonst ohne Badge, bis das Widget
 * geöffnet wird. Gleiche uid (Token-Refresh) und Start ohne Nutzer: nein.
 */
export function isUserChange(
  lastUid: string | null | undefined,
  uid: string | null,
): boolean {
  if (lastUid === undefined) return uid !== null;
  return uid !== lastUid;
}

/**
 * Daten des vorigen Nutzers verwerfen; mit neuem Nutzer frisch laden.
 * `reset()` lässt auch noch laufende Anfragen des vorigen Nutzers verfallen.
 */
export function resetTicketPortalStore(
  store: Pick<TicketPortalStore, 'reset' | 'refreshTickets'>,
  signedIn: boolean,
): void {
  store.reset();
  if (signedIn) void store.refreshTickets();
}
